import type {
  LanguageCode,
  PageLink,
  ProjectTranslation,
  UpdatedPageLinks,
  UpdatedProject,
  UpdatedProjectTranslation,
} from "../api/ProjectAPI.types";

type AnyProjectTranslation = ProjectTranslation | UpdatedProjectTranslation;

/**
 * Résout la valeur d'affichage d'un champ image traduit.
 * - `undefined` : aucun changement local → URL serveur
 * - `null` : suppression en attente → vide
 * - string : upload base64 en attente → la valeur
 */
export function resolveImageValue(
  pending: string | ArrayBuffer | null | undefined,
  serverUrl: string | null | undefined,
): string {
  if (pending !== undefined) {
    return typeof pending === "string" ? pending : "";
  }
  return serverUrl ?? "";
}

function rebuildLanguageIndexes(
  translations: AnyProjectTranslation[],
): UpdatedProject["translations_indexes"] {
  const language: Record<string, number[]> = {};
  let defaultIndex = 0;
  translations.forEach((translation, index) => {
    language[translation.language] = [index];
    if (translation.default) defaultIndex = index;
  });
  return {
    language,
    default: { true: translations.length > 0 ? [defaultIndex] : [] },
  };
}

/** Returns a new project with the given translations and their rebuilt indexes. */
function setTranslations(
  project: UpdatedProject,
  translations: UpdatedProject["translations"],
): UpdatedProject {
  return {
    ...project,
    translations,
    translations_indexes: rebuildLanguageIndexes(translations),
  };
}

function createProjectTranslation(
  languageCode: LanguageCode,
  overrides: Partial<UpdatedProjectTranslation> = {},
): UpdatedProjectTranslation {
  return {
    language: languageCode,
    default: false,
    title: "",
    description: "",
    copyright: "",
    logo_url: null,
    favicon_url: null,
    pictures: null,
    ...overrides,
  };
}

/** Returns a copy of the page link with the default language's title/url copied to `languageCode`. */
function addPageLinkLanguage(
  pageLink: PageLink | UpdatedPageLinks,
  defaultLanguageCode: LanguageCode,
  languageCode: LanguageCode,
): UpdatedPageLinks {
  const defaultLinkTranslation = pageLink.translations.find(
    (translation) => translation.language === defaultLanguageCode,
  );

  return {
    ...pageLink,
    translations: [
      ...pageLink.translations,
      {
        default: false,
        language: languageCode,
        title: defaultLinkTranslation?.title ?? "",
        url: defaultLinkTranslation?.url ?? "",
      },
    ],
  };
}

export function addProjectLanguage(
  project: UpdatedProject,
  languageCode: LanguageCode,
): UpdatedProject {
  if (
    project.translations.some(
      (translation) => translation.language === languageCode,
    )
  ) {
    return project;
  }

  const defaultIndex = project.translations_indexes.default.true[0] ?? 0;
  const defaultTranslation = project.translations[defaultIndex];

  const newTranslation = createProjectTranslation(languageCode, {
    title: defaultTranslation?.title ?? "",
    description: defaultTranslation?.description ?? "",
    copyright: defaultTranslation?.copyright ?? "",
  });

  const updated = setTranslations(project, [
    ...project.translations,
    newTranslation,
  ]);

  if (!defaultTranslation) return updated;

  return {
    ...updated,
    page_links: updated.page_links.map((pageLink) =>
      addPageLinkLanguage(pageLink, defaultTranslation.language, languageCode),
    ),
  };
}

export function removeProjectLanguage(
  project: UpdatedProject,
  languageCode: LanguageCode,
): UpdatedProject {
  const filtered = project.translations.filter(
    (translation) => translation.language !== languageCode,
  );
  const stillHasDefault = filtered.some((translation) => translation.default);

  // Si on a supprimé la langue par défaut, on promeut la première restante.
  const translations =
    filtered.length === 0 || stillHasDefault
      ? filtered
      : filtered.map((translation, index) => ({
          ...translation,
          default: index === 0,
        }));

  return setTranslations(project, translations);
}

export function seedDefaultLanguage(
  project: UpdatedProject,
  languageCode: LanguageCode,
): UpdatedProject {
  if (project.translations.length > 0) return project;

  return setTranslations(project, [
    createProjectTranslation(languageCode, { default: true }),
  ]);
}

export function setDefaultProjectLanguage(
  project: UpdatedProject,
  languageCode: LanguageCode,
): UpdatedProject {
  const translations = project.translations.map((translation) => ({
    ...translation,
    default: translation.language === languageCode,
  }));

  return setTranslations(project, translations);
}
